import React, { useCallback, useEffect, useState } from "react";
import ApiUnreachableModal from "../components/session/ApiUnreachableModal";
import {
  CSRPE_API_CONNECTIVITY_EVENT,
  resetApiConnectivityState,
} from "../services/apiConnectivity";
import { authAPI, getApiBaseUrl } from "../services/api";
import { API_HEALTH_TIMEOUT_MS } from "../config/apiTimeouts";
import { isAuthenticated, logout } from "../utils/auth";

/**
 * Global modal when the API is unreachable (hung process, wrong port, IIS recycle).
 * Works in development and production builds.
 */
export function ApiConnectivityProvider({ children, enabled = true }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const onConnectivity = (ev) => {
      const detail = ev?.detail || {};
      if (detail.open === true) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    window.addEventListener(CSRPE_API_CONNECTIVITY_EVENT, onConnectivity);
    return () => {
      window.removeEventListener(CSRPE_API_CONNECTIVITY_EVENT, onConnectivity);
    };
  }, [enabled]);

  /** Periodic lightweight ping so a hung API is detected even on a static page. */
  useEffect(() => {
    if (!enabled || !isAuthenticated()) return undefined;

    let cancelled = false;
    let failStreak = 0;

    const ping = async () => {
      if (cancelled) return;
      const base = getApiBaseUrl();
      const origin =
        base ||
        (typeof window !== "undefined" ? window.location.origin : "");
      const url = `${String(origin).replace(/\/+$/, "")}/api/health/ping`;
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), API_HEALTH_TIMEOUT_MS);
      try {
        const res = await fetch(url, {
          credentials: "include",
          signal: controller.signal,
          cache: "no-store",
        });
        window.clearTimeout(timer);
        if (!res.ok) throw new Error(`Health ping HTTP ${res.status}`);
        failStreak = 0;
        if (open) {
          resetApiConnectivityState();
        }
      } catch (err) {
        window.clearTimeout(timer);
        if (cancelled) return;
        // Ignore aborts from our own health timeout / unmount — not a real outage.
        if (
          err?.name === "AbortError" ||
          String(err?.message || "").toLowerCase().includes("aborted")
        ) {
          return;
        }
        failStreak += 1;
        if (failStreak >= 2) {
          setOpen(true);
        }
      }
    };

    // Defer first ping so initial page APIs (menu + SR list) are not competing on startup.
    const startId = window.setTimeout(ping, 10_000);
    const id = window.setInterval(ping, 60_000);
    return () => {
      cancelled = true;
      window.clearTimeout(startId);
      window.clearInterval(id);
    };
  }, [enabled]);

  const handleReload = useCallback(() => {
    resetApiConnectivityState();
    window.location.reload();
  }, []);

  const handleLogout = useCallback(async () => {
    resetApiConnectivityState();
    setOpen(false);
    try {
      await authAPI.logout();
    } catch {
      // cookie may already be gone when API is unreachable
    }
    logout();
  }, []);

  return (
    <>
      {children}
      {enabled && (
        <ApiUnreachableModal
          open={open}
          onReload={handleReload}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}

export default ApiConnectivityProvider;
